import {Camera, Scene, Vector3} from "three";
import {inject, Injectable} from "@angular/core";
import {SceneService} from "../service/Scene.service";
import {CameraService, CameraType} from "../service/camera.service";
import {RenderingStrategy} from "../service/rendering.service";
import {OrbitControls} from "three/examples/jsm/controls/OrbitControls";
import {ControlsService} from "../service/controls.service";
import {dispose3dResource} from "../util/dispose-object";

//typing
export type ViewPortManager = InstanceType<typeof ViewPortManagerStore>;

export type ViewPortWrapper = {
    id: string,
    viewPort?: HTMLElement
    camera?: Camera
    scene?: Scene
    renderingStrategy: RenderingStrategy,
    controls?: OrbitControls,
    isVisible?: boolean,
    previousControlState?: {
        target: Vector3;
        azimuthalAngle: number;
        polarAngle: number;
    };
}

export function newViewPortWrapper(obj: Partial<ViewPortWrapper>): ViewPortWrapper {
    if(obj.id === undefined) throw new Error('id is required');

    return {
        id: '',
        viewPort: undefined,
        camera: undefined,
        scene: undefined,
        renderingStrategy: RenderingStrategy.static,
        controls: undefined,
        isVisible: true,
        ...obj
    }
}

// ViewPortManagerStore is a signal store that holds the view port manager
// that is responsible for managing the view port of the ThreeJs scene
@Injectable({providedIn: 'root'})
export class ViewPortManagerStore {
    private readonly sceneService = inject(SceneService);
    private readonly cameraService = inject(CameraService);
    private readonly controlsService = inject(ControlsService);

    private readonly viewPorts: Array<ViewPortWrapper> = [];

    public async add(
        id: string,
        viewPort: HTMLElement,
        cameraType: CameraType,
        renderingStrategy?: RenderingStrategy
    ): Promise<void> {
        const scene = this.sceneService.new();
        const camera = this.cameraService.new(cameraType, viewPort);

        // Lisää kamera
        scene.add(camera);

        // Controls
        const controls: OrbitControls = this.controlsService.provideControls('orbit', { camera, viewPort });

        // Tallennetaan viewPort-tiedot
        this.viewPorts.push(newViewPortWrapper({
            id,
            viewPort,
            camera,
            scene,
            renderingStrategy,
            controls,
        }));
    }

    public getAll(): Array<ViewPortWrapper> { return this.viewPorts; }

    public getById(id: string): ViewPortWrapper {
        return this.viewPorts.find(vp => vp.id === id);
    }

    public isCycled(): boolean {
        return this.viewPorts.some(vp => vp.renderingStrategy === 'cycled');
    }

    public remove(id: string): void {
        const removed = this.viewPorts.splice(this.viewPorts.findIndex(vp => vp.id === id), 1);
        removed.forEach(vp => dispose3dResource(vp.scene));
    }

    public animateableViewPorts(): Array<ViewPortWrapper> {
        if(this.viewPorts === undefined) return [];
        return this.viewPorts.filter(vp => vp.renderingStrategy === 'animate' || vp.renderingStrategy === 'cycled');
    }

    public count(): number { return this.viewPorts.length; }

    public setVisibility(id: string, isVisible: boolean): void {
        const viewPort = this.getById(id);
        if(viewPort === undefined) return; // If viewPort is not found, example at build time.
        viewPort.isVisible = isVisible;
    }

    public setAllVisibility(isVisible: boolean): void {
        this.viewPorts.forEach(vp => {
            vp.isVisible = isVisible;
        });
    }

    private allVisibleViewPorts: Array<ViewPortWrapper>;

    public hideAllButThis(id: string): void {
        this.allVisibleViewPorts = this.viewPorts.filter(vp => vp.isVisible);
        this.allVisibleViewPorts.forEach(vp => {
            if(vp.id !== id) {
                vp.isVisible = false;
            }
        });

    }

    public restorePreviousVisibleState(){
        this.allVisibleViewPorts.forEach(vp => vp.isVisible = true);
    }

}
